import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Play,
  ArrowDown,
  AlertTriangle,
  Mail,
  Clock,
  Laptop,
  Youtube,
  Volume2,
} from "lucide-react";

import AuthoritySection from "../components/AuthoritySection";
import CaseStudies from "../components/CaseStudies";
import ReviewBadge from "../components/ReviewBadge";
import WebsiteCTA from "../components/WebsiteCTA";

const YT_ID = "rN6jWymoj1c";
const YT_PREVIEW = `https://www.youtube.com/embed/${YT_ID}?autoplay=1&mute=1&loop=1&playlist=${YT_ID}&controls=0&modestbranding=1&rel=0&playsinline=1&showinfo=0&disablekb=1`;
const YT_SOUND = `https://www.youtube.com/embed/${YT_ID}?autoplay=1&mute=0&controls=1&modestbranding=1&rel=0&playsinline=1`;
const YOUTUBE_CHANNEL_URL = "https://youtube.com/dsaecommerce";

const MUST_DO = [
  {
    icon: Mail,
    title: "Check your email + accept the Google Calendar invite",
    body: "Do it right now so the call is locked into your calendar.",
  },
  {
    icon: Clock,
    title: "Block off a full 30 minutes",
    body: "No distractions. This call decides whether you get a Q4 spot.",
  },
  {
    icon: Laptop,
    title: "Be on your laptop — NOT your phone",
    body: "You'll need a proper screen to see everything we cover.",
  },
  {
    icon: Play,
    title: "Watch this 9-minute video in full",
    body: "This is the single most important thing you can do before we talk.",
  },
];

export default function ThankYou() {
  const [withSound, setWithSound] = useState(false);

  useEffect(() => {
    const SRC = "https://www.trustcaptain.io/widget/dsaecommerce.com.js?v=badge";
    if (!document.querySelector(`script[src="${SRC}"]`)) {
      const s = document.createElement("script");
      s.src = SRC;
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Banner */}
      <div className="w-full bg-brand px-5 py-4 text-center">
        <p className="mx-auto max-w-4xl text-sm font-bold text-white md:text-base">
          7+ Years in Business&nbsp;&nbsp;|&nbsp;&nbsp;400+ Clients&nbsp;&nbsp;|&nbsp;&nbsp;Official Amazon &amp; Walmart Agency Partner
        </p>
      </div>

      {/* ===== Urgency hero ===== */}
      <main className="mx-auto max-w-3xl px-5 pt-10 text-center md:pt-14">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="mx-auto inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2"
          data-testid="thankyou-badge"
        >
          <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />
          <span className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-700">
            Call booked · You&apos;re on the list
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.06 }}
          className="mt-6 text-3xl font-black leading-[1.02] tracking-tight text-brand-ink sm:text-4xl md:text-5xl"
          data-testid="thankyou-headline"
        >
          WAIT! Watch This 9-Minute Video{" "}
          <span className="text-brand">Before Our Call</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.14 }}
          className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-neutral-600 md:text-lg"
        >
          Your qualification call is confirmed — but the people who show up{" "}
          <strong className="text-brand-ink">having watched this video</strong> get
          the most out of their spot. It&apos;s already playing 👇
        </motion.p>

        <div className="mt-6 flex items-center justify-center gap-3" aria-hidden="true">
          {[0, 0.15, 0.3].map((d) => (
            <motion.span
              key={d}
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1, repeat: Infinity, delay: d }}
            >
              <ArrowDown className="h-7 w-7 text-brand" strokeWidth={3} />
            </motion.span>
          ))}
        </div>
        <p className="mt-2 text-sm font-black uppercase tracking-[0.2em] text-red-600">
          Tap for sound — 9 min
        </p>
      </main>

      {/* ===== Video: autoplays muted, tap for sound ===== */}
      <section className="mx-auto mt-6 max-w-3xl px-5" data-testid="thankyou-video">
        <div className="relative aspect-video overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-900 shadow-[0_20px_60px_-20px_rgba(79,87,214,0.5)]">
          <iframe
            key={withSound ? "sound" : "muted"}
            src={withSound ? YT_SOUND : YT_PREVIEW}
            title="Watch this before our call"
            className="absolute inset-0 h-full w-full"
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
            allowFullScreen
            data-testid="thankyou-video-el"
          />
          {!withSound && (
            <button
              onClick={() => setWithSound(true)}
              data-testid="thankyou-unmute-btn"
              className="group absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/25 transition-colors duration-300 hover:bg-black/35"
              aria-label="Tap to watch with sound"
            >
              <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-red-600 text-white shadow-2xl transition-transform duration-300 group-hover:scale-110 md:h-24 md:w-24">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-600 opacity-40" />
                <Volume2 className="relative h-8 w-8 md:h-10 md:w-10" aria-hidden="true" />
              </span>
              <span className="rounded-full bg-black/75 px-5 py-2 text-sm font-bold uppercase tracking-widest text-white">
                🔊 Tap to watch with sound
              </span>
            </button>
          )}
        </div>
        <p className="mt-3 text-center text-sm text-neutral-500">
          The video is playing on mute — tap it to hear the full 9-minute breakdown.
        </p>
        <div className="mt-8">
          <ReviewBadge />
        </div>
      </section>

      {/* ===== MUST-DO checklist ===== */}
      <section className="mx-auto mt-14 max-w-3xl px-5" data-testid="mustdo-section">
        <div className="rounded-2xl border-2 border-red-200 bg-red-50/60 p-6 md:p-8">
          <div className="flex items-center justify-center gap-2 text-red-600">
            <AlertTriangle className="h-6 w-6" aria-hidden="true" />
            <h2 className="text-center text-lg font-black uppercase tracking-wide md:text-xl">
              Do these 4 things right now
            </h2>
          </div>
          <ul className="mt-6 space-y-4">
            {MUST_DO.map((m, i) => {
              const Icon = m.icon;
              return (
                <motion.li
                  key={m.title}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="flex items-start gap-4 rounded-xl bg-white p-4 shadow-sm"
                  data-testid={`mustdo-item-${i}`}
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
                    <Icon className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
                  </span>
                  <div>
                    <div className="font-bold text-brand-ink">{m.title}</div>
                    <p className="mt-0.5 text-sm text-neutral-600">{m.body}</p>
                  </div>
                </motion.li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ===== Authority: partner badges + stats ===== */}
      <div className="mt-16">
        <AuthoritySection />
      </div>

      {/* ===== Case studies: 50 real client dashboards ===== */}
      <CaseStudies />

      {/* ===== Prefer video? YouTube channel ===== */}
      <section className="mx-auto max-w-4xl px-5 py-16 md:py-20" data-testid="casestudies-section">
        <a
          href={YOUTUBE_CHANNEL_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="youtube-link"
          className="group mx-auto flex max-w-xl flex-col items-center gap-4 rounded-2xl border border-neutral-200 bg-white p-8 text-center transition-all duration-300 hover:-translate-y-1 hover:border-red-300 hover:shadow-lg"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-600 text-white">
            <Youtube className="h-7 w-7" aria-hidden="true" />
          </span>
          <h3 className="text-xl font-bold text-brand-ink">
            Prefer video? Watch client interviews on YouTube
          </h3>
          <p className="max-w-md text-sm text-neutral-600">
            Hear directly from store owners about their results, month after month.
          </p>
          <span className="mt-1 inline-flex items-center gap-2 font-bold text-red-600">
            youtube.com/dsaecommerce
            <ArrowDown className="h-4 w-4 -rotate-90 transition-transform group-hover:translate-x-1" aria-hidden="true" />
          </span>
        </a>
      </section>

      {/* ===== TrustCaptain 5-star ratings ===== */}
      <section className="w-full bg-obsidian px-5 py-16" data-testid="trustcaptain-section">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-2xl font-black text-white md:text-3xl">
            Rated 5 stars by our clients
          </h2>
          <p className="mt-2 text-sm text-neutral-400">Verified reviews via TrustCaptain</p>
          <div className="mt-8 flex justify-center">
            <div id="trustcaptain-widget" data-domain="dsaecommerce.com" data-testid="trustcaptain-widget" />
          </div>
        </div>
      </section>

      {/* ===== Visit main website (replaces back-to-application) ===== */}
      <WebsiteCTA />
    </div>
  );
}
